"use client";

import { useState } from "react";
import {
    Link,
    FileText,
    AlignLeft,
    Pencil,
    Trash2,
    ExternalLink,
    Download,
    Plus,
    File,
    FileImage,
    Presentation,
    Sheet,
    Eye,
} from "lucide-react";
import ResourceModal from "./ResourceModal";
import ResourceViewerModal from "./ResourceViewerModal";
import type { ResourceFormData } from "@/lib/types/resources";

export interface Resource {
    id: number;
    title: string;
    type: "LINK" | "FILE" | "RICH_TEXT";
    content: string;
    course_id: number | null;
    module_id: number | null;
    lesson_id: number | null;
    file_name: string | null;
    file_size: number | null;
    mime_type: string | null;
    embeddable: boolean | null;
    display_order: number;
    created_at: string;
    modules?: { id: number; title: string } | null;
    lessons?: { id: number; title: string } | null;
}

interface CourseModule {
    id: number;
    title: string;
    levelName: string | null;
    lessons: { id: number; title: string }[];
}

interface ResourceListProps {
    resources: Resource[];
    courseId: number;
    modules?: CourseModule[];
    onResourceCreated: (data: ResourceFormData | FormData) => Promise<void>;
    onResourceUpdated: (id: number, data: Partial<ResourceFormData> | FormData) => Promise<void>;
    onResourceDeleted: (id: number) => Promise<void>;
}

function formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileIcon(mimeType: string | null) {
    if (!mimeType) return <File className="h-5 w-5 text-slate-400" />;
    if (mimeType.startsWith("image/")) return <FileImage className="h-5 w-5 text-purple-500" />;
    if (mimeType === "application/pdf") return <FileText className="h-5 w-5 text-red-500" />;
    if (mimeType.includes("powerpoint") || mimeType.includes("presentation"))
        return <Presentation className="h-5 w-5 text-orange-500" />;
    if (mimeType.includes("excel") || mimeType.includes("spreadsheet"))
        return <Sheet className="h-5 w-5 text-emerald-600" />;
    return <FileText className="h-5 w-5 text-blue-500" />;
}

function ScopeBadge({ resource }: { resource: Resource }) {
    if (resource.lesson_id && resource.lessons) {
        return (
            <span className="inline-flex items-center gap-1 text-[10px] font-medium text-violet-600">
                <span className="h-1.5 w-1.5 rounded-full bg-violet-400" />
                {resource.lessons.title}
            </span>
        );
    }
    if (resource.module_id && resource.modules) {
        return (
            <span className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-600">
                <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
                {resource.modules.title}
            </span>
        );
    }
    return null;
}

function LinkCard({ resource, onEdit, onDelete, onView }: { resource: Resource; onEdit: () => void; onDelete: () => void; onView: () => void }) {
    const [deleting, setDeleting] = useState(false);

    const handleDelete = async () => {
        setDeleting(true);
        try { await onDelete(); } finally { setDeleting(false); }
    };

    return (
        <div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3">
            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-indigo-50">
                <Link className="h-5 w-5 text-indigo-600" />
            </div>
            <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-slate-900">{resource.title}</p>
                <p className="mt-0.5 truncate text-xs text-slate-400">{resource.content}</p>
            </div>
            <div className="flex items-center gap-1.5">
                <button
                    type="button"
                    onClick={onView}
                    className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                >
                    <Eye className="h-3.5 w-3.5" />
                    View
                </button>
                <a
                    href={resource.content}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-md border border-slate-200 bg-white p-1.5 text-slate-500 hover:bg-slate-50"
                >
                    <ExternalLink className="h-3.5 w-3.5" />
                </a>
                <button
                    type="button"
                    onClick={onEdit}
                    className="rounded-md border border-slate-200 bg-white p-1.5 text-slate-500 hover:bg-slate-50"
                >
                    <Pencil className="h-3.5 w-3.5" />
                </button>
                <button
                    type="button"
                    onClick={handleDelete}
                    disabled={deleting}
                    className="rounded-md border border-red-200 bg-red-50 p-1.5 text-red-600 hover:bg-red-100 disabled:opacity-50"
                >
                    <Trash2 className="h-3.5 w-3.5" />
                </button>
            </div>
        </div>
    );
}

function FileCard({ resource, onEdit, onDelete, onView }: { resource: Resource; onEdit: () => void; onDelete: () => void; onView: () => void }) {
    const [deleting, setDeleting] = useState(false);

    const handleDelete = async () => {
        setDeleting(true);
        try { await onDelete(); } finally { setDeleting(false); }
    };

    return (
        <div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3">
            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-slate-50">
                {getFileIcon(resource.mime_type)}
            </div>
            <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-slate-900">{resource.title}</p>
                <p className="mt-0.5 text-xs text-slate-400">
                    {resource.file_name ?? "File"}
                    {resource.file_size != null && ` · ${formatFileSize(resource.file_size)}`}
                </p>
            </div>
            <div className="flex items-center gap-1.5">
                <button
                    type="button"
                    onClick={onView}
                    className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                >
                    <Eye className="h-3.5 w-3.5" />
                    View
                </button>
                <a
                    href={resource.content}
                    download={resource.file_name ?? true}
                    className="rounded-md border border-slate-200 bg-white p-1.5 text-slate-500 hover:bg-slate-50"
                >
                    <Download className="h-3.5 w-3.5" />
                </a>
                <button
                    type="button"
                    onClick={onEdit}
                    className="rounded-md border border-slate-200 bg-white p-1.5 text-slate-500 hover:bg-slate-50"
                >
                    <Pencil className="h-3.5 w-3.5" />
                </button>
                <button
                    type="button"
                    onClick={handleDelete}
                    disabled={deleting}
                    className="rounded-md border border-red-200 bg-red-50 p-1.5 text-red-600 hover:bg-red-100 disabled:opacity-50"
                >
                    <Trash2 className="h-3.5 w-3.5" />
                </button>
            </div>
        </div>
    );
}

function RichTextCard({ resource, onEdit, onDelete, onView }: { resource: Resource; onEdit: () => void; onDelete: () => void; onView: () => void }) {
    const [deleting, setDeleting] = useState(false);

    const handleDelete = async () => {
        setDeleting(true);
        try { await onDelete(); } finally { setDeleting(false); }
    };

    const plainPreview = resource.content
        .replace(/<[^>]+>/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .slice(0, 100);

    return (
        <div className="rounded-lg border border-slate-200 bg-white">
            <div className="flex items-center gap-3 px-4 py-3">
                <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-emerald-50">
                    <AlignLeft className="h-5 w-5 text-emerald-600" />
                </div>
                <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-900">{resource.title}</p>
                    <p className="mt-0.5 truncate text-xs text-slate-400">{plainPreview}...</p>
                </div>
                <div className="flex items-center gap-1.5">
                    <button
                        type="button"
                        onClick={onView}
                        className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                    >
                        <Eye className="h-3.5 w-3.5" />
                        View
                    </button>
                    <button
                        type="button"
                        onClick={onEdit}
                        className="rounded-md border border-slate-200 bg-white p-1.5 text-slate-500 hover:bg-slate-50"
                    >
                        <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                        type="button"
                        onClick={handleDelete}
                        disabled={deleting}
                        className="rounded-md border border-red-200 bg-red-50 p-1.5 text-red-600 hover:bg-red-100 disabled:opacity-50"
                    >
                        <Trash2 className="h-3.5 w-3.5" />
                    </button>
                </div>
            </div>
        </div>
    );
}

function SectionHeader({ label, level }: { label: string; level: "course" | "level" | "module" | "lesson" }) {
    const indent = level === "lesson" ? "pl-3" : level === "module" ? "pl-3" : "";
    return (
        <div className={`flex items-center gap-2 ${indent} pt-2 pb-1`}>
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                {label}
            </span>
            <div className="flex-1 border-t border-slate-100" />
        </div>
    );
}

export default function ResourceList({
    resources,
    courseId,
    modules = [],
    onResourceCreated,
    onResourceUpdated,
    onResourceDeleted,
}: ResourceListProps) {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingResource, setEditingResource] = useState<Resource | null>(null);
    const [viewingResource, setViewingResource] = useState<Resource | null>(null);

    const handleSave = async (data: ResourceFormData | FormData) => {
        if (editingResource) {
            await onResourceUpdated(editingResource.id, data);
        } else {
            await onResourceCreated(data);
        }
        setEditingResource(null);
    };

    const openCreate = () => {
        setEditingResource(null);
        setIsModalOpen(true);
    };

    const openEdit = (resource: Resource) => {
        setEditingResource(resource);
        setIsModalOpen(true);
    };

    return (
        <div>
            <div className="mb-4 flex items-center justify-between">
                <p className="text-sm text-slate-500">
                    {resources.length === 0
                        ? "No resources attached yet."
                        : `${resources.length} resource${resources.length === 1 ? "" : "s"}`}
                </p>
                <button
                    type="button"
                    onClick={openCreate}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
                >
                    <Plus className="h-4 w-4" />
                    Add resource
                </button>
            </div>

            {resources.length > 0 && (
                <div className="space-y-1">
                    {(() => {
                        const renderCard = (resource: Resource) => {
                            const props = {
                                resource,
                                onEdit: () => openEdit(resource),
                                onDelete: () => onResourceDeleted(resource.id),
                                onView: () => setViewingResource(resource),
                            };
                            if (resource.type === "LINK") return <LinkCard key={resource.id} {...props} />;
                            if (resource.type === "FILE") return <FileCard key={resource.id} {...props} />;
                            return <RichTextCard key={resource.id} {...props} />;
                        };

                        const byModule = new Map<number, Resource[]>();
                        const byLesson = new Map<number, Resource[]>();
                        const courseLevel: Resource[] = [];

                        for (const r of resources) {
                            if (r.lesson_id) {
                                const arr = byLesson.get(r.lesson_id) ?? [];
                                arr.push(r);
                                byLesson.set(r.lesson_id, arr);
                            } else if (r.module_id) {
                                const arr = byModule.get(r.module_id) ?? [];
                                arr.push(r);
                                byModule.set(r.module_id, arr);
                            } else {
                                courseLevel.push(r);
                            }
                        }

                        const sections: React.ReactNode[] = [];

                        if (courseLevel.length > 0) {
                            sections.push(<SectionHeader key="hdr-course" label="Course" level="course" />);
                            sections.push(<div key="sec-course" className="space-y-2">{courseLevel.map(renderCard)}</div>);
                        }

                        // Group modules by level
                        const byLevel = new Map<string, typeof modules>();
                        for (const mod of modules) {
                            const levelKey = mod.levelName ?? "no-level";
                            const arr = byLevel.get(levelKey) ?? [];
                            arr.push(mod);
                            byLevel.set(levelKey, arr);
                        }

                        // Render by level, then module, then lesson
                        for (const [levelKey, levelModules] of byLevel) {
                            let hasLevelContent = false;
                            const levelSections: React.ReactNode[] = [];

                            for (const mod of levelModules) {
                                const modResources = byModule.get(mod.id) ?? [];
                                const hasModResources = modResources.length > 0;
                                const lessonSections: React.ReactNode[] = [];

                                for (const lesson of mod.lessons) {
                                    const lessonResources = byLesson.get(lesson.id) ?? [];
                                    if (lessonResources.length === 0) continue;
                                    lessonSections.push(<SectionHeader key={`hdr-les-${lesson.id}`} label={lesson.title} level="lesson" />);
                                    lessonSections.push(<div key={`sec-les-${lesson.id}`} className="space-y-2 pl-3">{lessonResources.map(renderCard)}</div>);
                                }

                                if (!hasModResources && lessonSections.length === 0) continue;

                                hasLevelContent = true;
                                levelSections.push(<SectionHeader key={`hdr-mod-${mod.id}`} label={mod.title} level="module" />);
                                if (hasModResources) {
                                    levelSections.push(<div key={`sec-mod-${mod.id}`} className="space-y-2">{modResources.map(renderCard)}</div>);
                                }
                                if (lessonSections.length > 0) {
                                    levelSections.push(<div key={`les-group-${mod.id}`} className="space-y-1">{lessonSections}</div>);
                                }
                            }

                            if (hasLevelContent) {
                                if (levelKey !== "no-level") {
                                    sections.push(<SectionHeader key={`hdr-level-${levelKey}`} label={levelKey} level="level" />);
                                }
                                sections.push(<div key={`level-group-${levelKey}`} className="space-y-1">{levelSections}</div>);
                            }
                        }

                        return sections;
                    })()}
                </div>
            )}

            <ResourceModal
                isOpen={isModalOpen}
                onClose={() => { setIsModalOpen(false); setEditingResource(null); }}
                onSave={handleSave}
                courseId={courseId}
                modules={modules}
                initialData={editingResource ?? undefined}
            />

            <ResourceViewerModal
                resource={viewingResource}
                isOpen={viewingResource !== null}
                onClose={() => setViewingResource(null)}
            />
        </div>
    );
}
